import React from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { formatExchangeRate } from '../../../utils/formatExchangeRate';
import styles from '../../settings/configuration/shared/IntelligencePanel.module.scss';
import { INTEL_PAY_LABELS } from './hireTypes';
import type { IntelligenceHireApi } from './useIntelligenceHire';

interface IntelligencePurchaseModalProps {
  hire: IntelligenceHireApi;
}

const IntelligencePurchaseModal: React.FC<IntelligencePurchaseModalProps> = ({
  hire,
}) => {
  const {
    purchase,
    isCapacitySwitch,
    currentCapacityId,
    packQty,
    setPackQty,
    purchaseAmountUSD,
    allowedPayMethods,
    payMethod,
    setPayMethod,
    vesError,
    setVesError,
    vesExchangeSources,
    vesExchangeRate,
    setVesExchangeRate,
    vesCaptureFile,
    setVesCaptureFile,
    closePurchase,
    confirmPurchase,
  } = hire;

  return (
    <Modal
      show={Boolean(purchase)}
      onHide={closePurchase}
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {purchase?.kind === 'pack'
            ? 'Comprar créditos'
            : isCapacitySwitch
              ? `Cambiar a ${purchase?.name || 'Intelligence'}`
              : `Activar ${purchase?.name || 'Intelligence'}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {vesError && (
          <Alert variant="danger" dismissible onClose={() => setVesError(null)}>
            {vesError}
          </Alert>
        )}
        <div className="border rounded p-3 mb-3">
          <div className="fw-semibold">{purchase?.name}</div>
          {purchase?.kind === 'pack' ? (
            <div className="text-muted small">
              {Number(purchase.credits || 0).toLocaleString()} créditos · $
              {purchase.unitPrice.toFixed(2)} c/u
            </div>
          ) : (
            <div className="text-muted small">
              {typeof purchase?.credits === 'number' && purchase.credits > 0
                ? `${purchase.credits.toLocaleString()} créditos / mes · `
                : ''}
              ${Number(purchase?.unitPrice || 0).toFixed(2)} USD / mes
            </div>
          )}
        </div>
        {purchase?.kind === 'pack' ? (
          <Form.Group className="mb-3">
            <Form.Label>Cantidad</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={50}
              value={packQty}
              onChange={e => {
                const n = Number(e.target.value);
                setPackQty(
                  Number.isFinite(n)
                    ? Math.max(1, Math.min(50, Math.floor(n)))
                    : 1
                );
              }}
            />
            <Form.Text muted>
              Total: ${purchaseAmountUSD.toFixed(2)} USD · aplica al periodo
              actual.
            </Form.Text>
          </Form.Group>
        ) : (
          <p className="text-muted small mb-3">
            {isCapacitySwitch
              ? `Vas a cambiar Intelligence de ${String(currentCapacityId).toUpperCase()} a ${String(purchase?.capacityId).toUpperCase()}.`
              : 'Se agrega como complemento de tu plan MayorX. No crea una segunda suscripción.'}
          </p>
        )}
        <div className={styles.payMethods} role="radiogroup" aria-label="Método de pago">
          <span className={styles.payMethodsLabel}>Método de pago</span>
          <div className={styles.payMethodsRow}>
            {allowedPayMethods.map(method => (
              <button
                key={method}
                type="button"
                role="radio"
                aria-checked={payMethod === method}
                className={`${styles.payMethodBtn} ${
                  payMethod === method ? styles.payMethodBtnActive : ''
                }`}
                onClick={() => {
                  setPayMethod(method);
                  setVesError(null);
                }}
              >
                {INTEL_PAY_LABELS[method]}
              </button>
            ))}
          </div>
        </div>
        {payMethod === 'ves' ? (
          <>
            <div className="bg-body-tertiary border rounded p-3 mb-3">
              <p className="mb-1 fw-semibold">Cuenta Banesco para transferir</p>
              <p className="mb-1 small text-muted">Pago móvil o transferencia</p>
              <p className="mb-1 font-monospace">01340394583941007200</p>
              <p className="mb-1">CI: 7101690 · 04144182515</p>
              <p className="mb-0">Ardon Esteban Hernández Reyes</p>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Tipo de cambio (USD → VES)</Form.Label>
              {vesExchangeSources.length > 0 ? (
                <Form.Select
                  value={vesExchangeRate}
                  onChange={e => setVesExchangeRate(Number(e.target.value))}
                >
                  {vesExchangeSources.map(src => (
                    <option key={src.id} value={src.rate}>
                      {src.name}: 1 USD = {formatExchangeRate(src.rate)} VES
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control
                  type="number"
                  min={1}
                  value={vesExchangeRate || ''}
                  onChange={e => setVesExchangeRate(Number(e.target.value) || 0)}
                  placeholder="Ingresa el tipo de cambio"
                />
              )}
              <div className="mt-2 p-2 rounded bg-body-secondary">
                <span className="text-muted small">Monto a transferir:</span>
                <p className="mb-0 fs-5 fw-bold">
                  {(purchaseAmountUSD * vesExchangeRate).toLocaleString('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 3,
                  })}{' '}
                  Bs
                </p>
                <span className="text-muted small">
                  (${purchaseAmountUSD.toFixed(2)} USD)
                </span>
              </div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Captura de la transacción</Form.Label>
              <Form.Control
                type="file"
                accept="image/*,.pdf"
                onChange={e => {
                  const input = e.target as HTMLInputElement;
                  setVesCaptureFile(input.files?.[0] ?? null);
                }}
              />
              <Form.Text muted>
                Sube una imagen o PDF del comprobante de transferencia.
              </Form.Text>
            </Form.Group>
          </>
        ) : (
          <p className="text-muted small mb-0">
            El cargo se añade a tu suscripción Stripe existente. Puede haber
            prorrateo.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={closePurchase}>
          Cancelar
        </Button>
        <Button
          variant={payMethod === 'ves' ? 'warning' : 'primary'}
          onClick={confirmPurchase}
          disabled={
            payMethod === 'ves' && (!vesCaptureFile || vesExchangeRate <= 0)
          }
        >
          {payMethod === 'ves'
            ? 'Enviar solicitud'
            : purchase?.kind === 'pack'
              ? 'Comprar créditos'
              : isCapacitySwitch
                ? 'Cambiar capacidad'
                : 'Confirmar y agregar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default IntelligencePurchaseModal;
